import { useTheme, type ThemePreference } from '../../theme/useTheme'

const NEXT_THEME: Record<ThemePreference, ThemePreference> = { light: 'dark', dark: 'system', system: 'light' }
const THEME_LABEL: Record<ThemePreference, string> = { light: 'light', dark: 'dark', system: 'system' }

/**
 * One small icon button, cycling Light, Dark, System, the same cycle
 * docs/design/style-guide.md's theme control names, so a signed-out visitor
 * can reach System too rather than being stuck on an explicit choice once
 * they have made one.
 *
 * Icon only, so the accessible name does the work: it says what pressing will
 * do rather than what the current state is, because a control you have to press
 * to understand is a guess. A live region announces the result, since the only
 * visual feedback is the icon swapping; it names the preference chosen, System
 * included, rather than only the theme it currently resolves to.
 */
export default function ThemeToggle() {
  const { preference, resolved, choose } = useTheme()

  const next = NEXT_THEME[preference]
  const description = `Switch to ${THEME_LABEL[next]} theme`

  return (
    <>
      <button
        type="button"
        onClick={() => choose(next)}
        aria-label={description}
        title={description}
        className="inline-flex cursor-pointer items-center justify-center rounded p-2 text-ink-muted hover:bg-surface hover:text-ink"
      >
        {resolved === 'dark' ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </svg>
        )}
      </button>

      <span role="status" aria-live="polite" className="sr-only">
        {preference === 'system'
          ? `System theme, currently ${resolved}`
          : `${preference === 'dark' ? 'Dark' : 'Light'} theme`}
      </span>
    </>
  )
}
